// Claude: A1 — Upload Reports admin page — history of all bulk CSV uploads 26-02-2026
import React, { useEffect, useState } from "react";
import { Box, Button, Flex, Text, Badge } from "@chakra-ui/react";
import { Table, Modal, Select } from "antd";
import { AdminAPI } from "../../Apis/adminAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";
import moment from "moment";
import Loader from "../../Components/Loader/Loader";

const uploadTypeLabel: Record<string, string> = {
  "standalone-seller": "Seller (standalone)",
  "standalone-buyer": "Buyer (standalone)",
  "event-seller": "Exhibitor (event)",
  "event-buyer": "Visitor (event)",
};

const UploadReports = () => {
  const { error, success } = useToastWrapper();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState<any[]>([]);
  const [filterType, setFilterType] = useState<string>("");
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await AdminAPI.getUploadReports();
      setReports(Array.isArray(res) ? res : (res?.data ?? []));
    } catch (err) {
      error(errorHandler(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      await AdminAPI.deleteUploadReport(deleteTarget._id);
      success("Report deleted", "");
      setDeleteTarget(null);
      fetchReports();
    } catch (err) {
      error(errorHandler(err));
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = (report: any) => {
    const headers = ["Email", "Company Name", "Status", "Reason"];
    const rows = report.rows.map((r: any) =>
      [r.email, r.companyName, r.status, r.reason]
        .map((v) => `"${(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `upload-report-${report.uploadType}-${moment(report.createdAt).format("YYYYMMDD-HHmmss")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = filterType
    ? reports.filter((r) => r.uploadType === filterType)
    : reports;

  const statusBadge = (record: any) => {
    const s = record.processingStatus ?? "complete";
    if (s === "pending") return <Badge colorScheme="yellow" px={2}>Processing</Badge>;
    if (s === "failed") return <Badge colorScheme="red" px={2}>Failed</Badge>;
    return <Badge colorScheme="green" px={2}>Complete</Badge>;
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v: string) => moment(v).format("DD MMM YYYY, HH:mm"),
    },
    {
      title: "Type",
      dataIndex: "uploadType",
      key: "uploadType",
      render: (v: string) => uploadTypeLabel[v] ?? v,
    },
    {
      title: "Event ID",
      dataIndex: "eventId",
      key: "eventId",
      render: (v: string) => v || "—",
    },
    {
      title: "Status",
      key: "processingStatus",
      render: (_: any, record: any) => statusBadge(record),
    },
    {
      title: "Created",
      dataIndex: "createdCount",
      key: "createdCount",
      render: (v: number) => (
        <Badge colorScheme="green" px={2}>{v}</Badge>
      ),
    },
    {
      title: "Merged",
      dataIndex: "updatedCount",
      key: "updatedCount",
      render: (v: number) => (
        <Badge colorScheme="blue" px={2}>{v ?? 0}</Badge>
      ),
    },
    {
      title: "Failed",
      dataIndex: "failedCount",
      key: "failedCount",
      render: (v: number) => (
        <Badge colorScheme="red" px={2}>{v}</Badge>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Flex gap={2} flexDirection="column" alignItems="flex-start">
          <Flex gap={2}>
            <Button
              h="auto"
              px={3}
              py={1}
              bg={record.processingStatus === "pending" ? "gray.300" : "#0E122F"}
              color={record.processingStatus === "pending" ? "gray.500" : "white"}
              fontSize="sm"
              cursor={record.processingStatus === "pending" ? "not-allowed" : "pointer"}
              _hover={{ bg: record.processingStatus === "pending" ? "gray.300" : "#2E7D32" }}
              onClick={() => record.processingStatus !== "pending" && downloadCsv(record)}
            >
              Download
            </Button>
            <Button
              h="auto"
              px={3}
              py={1}
              bg="#0E122F"
              color="white"
              fontSize="sm"
              _hover={{ bg: "#C62828" }}
              onClick={() => setDeleteTarget(record)}
            >
              Delete
            </Button>
          </Flex>
          {record.processingStatus === "failed" && record.systemError && (
            <Text fontSize="xs" color="red.600" maxW="260px">
              Error: {record.systemError}
            </Text>
          )}
        </Flex>
      ),
    },
  ];

  return (
    <div className="p-4">
      {loading && <Loader />}

      {/* Delete confirmation modal */}
      <Modal
        title="Delete Report"
        footer={null}
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
      >
        <Text my={5} mt={8}>
          Are you sure you want to permanently delete this upload report?
        </Text>
        <Flex justifyContent="flex-end" gap={3}>
          <Button
            h="auto" px={4} py={2} variant="outline" borderColor="#0E122F" color="#0E122F"
            _hover={{ bg: "gray.100" }}
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
          </Button>
          <Button
            h="auto" px={4} py={2} bg="#C62828" color="white"
            _hover={{ bg: "red.800" }}
            onClick={handleDelete}
          >
            Yes, Delete
          </Button>
        </Flex>
      </Modal>

      <Flex justifyContent="space-between" alignItems="center" mt="10px" mb={6}>
        <Text fontSize="lg" fontWeight={600}>Upload Reports</Text>
        <Select
          style={{ width: 200, height: 35 }}
          placeholder="Filter by type"
          allowClear
          onChange={(v) => setFilterType(v ?? "")}
          options={[
            { label: "Seller (standalone)", value: "standalone-seller" },
            { label: "Buyer (standalone)", value: "standalone-buyer" },
            { label: "Exhibitor (event)", value: "event-seller" },
            { label: "Visitor (event)", value: "event-buyer" },
          ]}
        />
      </Flex>

      <Box>
        {filtered.length === 0 && !loading ? (
          <Text color="gray.500">No upload reports found.</Text>
        ) : (
          <Table
            dataSource={filtered}
            columns={columns}
            rowKey="_id"
            bordered
            pagination={{ pageSize: 20 }}
          />
        )}
      </Box>
    </div>
  );
};

export default UploadReports;
